import { RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import { AXIOS, AxiosService } from '@umanforge/common';

import { Input } from './input.component';
import { Button } from './button.component';
import utils from '../features/utils';
import { API } from '../constants/api.constants';
import { NeedChangePassword } from './change_password.component';
import { ToastService } from '@common_services/toast.service';

const toastService: ToastService = new ToastService( 'Forgot Password' );

interface ForgotPasswordProps {
  onClickBackLogin: () => void;
  onAxiosError: ( err: AxiosError ) => void;
}
interface SendCodeProps {
  setUserId: ( userId: string ) => void;
  setEmail: ( email: string ) => void;
  onAxiosError: ( err: AxiosError ) => void;
}
interface VerifyCodeProps {
  setCode: ( code: string ) => void;
  email: string;
  userId: string;
  onAxiosError: ( err: AxiosError ) => void;
}

export const ForgotPassword = ( { onClickBackLogin, onAxiosError }: ForgotPasswordProps ) => {
  const { t } = useTranslation();

  const [ userId, setUserId ] = useState<string>( '' );
  const [ email, setEmail ] = useState<string>( '' );
  const [ code, setCode ] = useState<string>( '' );

  const onClickSavePassword = ( password: string ): Promise<boolean> => {
    return new Promise( resolve => {
      AXIOS.post( API.SET_NEW_PASSWORD_CODE, { id: userId, code, password } )
        .then( response => {
          if ( response.status === 201 ) {
            toast( t( 'Your password has been changed !' ), { type: 'success' } );
            onClickBackLogin();
          } else if ( response.status === 202 ) {
            toastService.showWarning( AxiosService.getAxiosMessage( response ) );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          onAxiosError( err );
          resolve( false );
        } );
    } );
  };

  return (
    <div className="flex flex-col h-full w-[300px] text-sm text-gray-600 gap-8 justify-center">
      <div
        className='flex gap-1 place-items-center text-xs select-none cursor-pointer text-blue-600 hover:text-blue-800 fill-blue-600 hover:fill-blue-800'
        onClick={ onClickBackLogin }
      >
        <svg
          viewBox="0 0 448 512"
          className='w-4'
        >
          <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
        </svg>
        <p> { t( 'Back to login' ) } </p>
      </div>
      <div className='h-full m-2'>
        {
          ( userId == '' ) ?
            <SendCode
              setUserId={ setUserId }
              setEmail={ setEmail }
              onAxiosError={ onAxiosError }
            />
            :
            (
              ( code == '' ) ?
                <VerifyCode
                  setCode={ setCode }
                  userId={ userId }
                  email={ email }
                  onAxiosError={ onAxiosError }
                />
                :
                <NeedChangePassword
                  title={ t( 'Please enter your new password' ) }
                  buttonText={ t( 'Reset password' ) }
                  onClick={ onClickSavePassword }
                  onAxiosError={ onAxiosError }
                />
            )
        }
      </div>

    </div>
  );
};

const SendCode = ( { setUserId, setEmail, onAxiosError }: SendCodeProps ) => {
  const { t } = useTranslation();

  const [ errorMessage, setErrorMessage ] = useState<string>( '' );
  const [ canSendEmail, setCanSendEmail ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  const emailRef = useRef<HTMLInputElement>( null );

  const checkValidEmail = ( value: string ): string | null => {
    let error = null;
    if ( value === '' ) {
      error = 'The field must not be empty !';
    } else if ( !utils.isValidEmail( value ) ) {
      error = 'Please enter an email address !';
    }
    return error;
  };

  const onChange = () => {
    const input = emailRef as RefObject<HTMLInputElement>;
    setCanSendEmail( checkValidEmail( input.current?.value ?? '' ) == null );
  };

  const sendCode = () => {
    setErrorMessage( '' );
    setIsLoading( true );
    const input = emailRef as RefObject<HTMLInputElement>;
    const email = String( input.current?.value );
    AXIOS.get( API.SEND_PASSWORD_CODE.replace( ':email', email ) )
      .then( response => {
        if ( response.status === 200 ) {
          setUserId( response.data.id );
          setEmail( email );
        } else if ( response.status === 202 ) {
          setErrorMessage( AxiosService.getAxiosMessage( response ) );
          setUserId( '' );
          setEmail( '' );
        }
      } )
      .catch( ( err: AxiosError ) => {
        setUserId( '' );
        onAxiosError( err );
      } )
      .finally( () => {
        setIsLoading( false );
      } );
  };

  return (
    <div className='flex flex-col h-full justify-between'>
      <div className='flex flex-col h-fit gap-4'>
        <p> { t( 'Please enter your email address to receive a code and reset your password.' ) } </p>
        <div className="mx-4">
          <Input
            label={ t( 'Email' ) }
            placeholder='exemple@prod-eo.com'
            ref={ emailRef }
            onChange={ onChange }
            checkContent={ checkValidEmail }
            maxLength={ 320 }
          />
        </div>
        {
          errorMessage ?
            <p className='text-sm text-center text-error' >
              { errorMessage }
            </p>
            :
            null
        }
      </div>
      <div className='flex justify-center h-fit w-full'>
        <div className='w-2/3 min-w-[150px] h-9'>
          <Button
            label={ t( 'Send code' ) }
            action={ sendCode }
            disable={ !canSendEmail }
            isLoading={ isLoading }
          />
        </div>
      </div>
    </div>
  );
};

const VerifyCode = ( { setCode, userId, email, onAxiosError }: VerifyCodeProps ) => {
  const { t } = useTranslation();

  const [ errorMessage, setErrorMessage ] = useState<string>( '' );
  const [ canVerifyCode, setCanVerifyCode ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  const codeRef = useRef<HTMLInputElement>( null );

  const checkValidCode = ( value: string ): string | null => {
    let error = null;
    if ( value.length === 0 ) {
      error = 'The field must not be empty !';
    } else if ( value.length !== 8 ) {
      error = 'Wrong code format';
    }
    return error;
  };

  const onChange = () => {
    const input = codeRef as RefObject<HTMLInputElement>;
    setCanVerifyCode( checkValidCode( input.current?.value ?? '' ) == null );
  };

  const verifyCode = () => {
    setErrorMessage( '' );
    setIsLoading( true );
    const input = codeRef as RefObject<HTMLInputElement>;
    const code = String( input.current?.value );
    AXIOS.post( API.VERIFY_PASSWORD_CODE, { id: userId, code: code } )
      .then( response => {
        if ( response.status === 204 ) {
          setCode( code );
        } else if ( response.status === 202 ) {
          setErrorMessage( AxiosService.getAxiosMessage( response ) );
          setCode( '' );
        }
      } )
      .catch( ( err: AxiosError ) => {
        setCode( '' );
        onAxiosError( err );
      } )
      .finally( () => {
        setIsLoading( false );
      } );
  };

  return (
    <div className='flex flex-col h-full justify-between'>
      <div className='flex flex-col h-fit gap-4'>
        <p> { t( 'Please enter the code received on the e-mail \'{0}\'.' ).replace( '{0}', email ) } </p>
        <div className="mx-4">
          <Input
            label={ t( 'Code' ) }
            placeholder='A1B2C3D4'
            ref={ codeRef }
            onChange={ onChange }
            checkContent={ checkValidCode }
          />
        </div>
        {
          errorMessage ?
            <p className='text-sm text-center text-error' >
              { errorMessage }
            </p>
            :
            null
        }
      </div>
      <div className='flex justify-center w-full'>
        <div className='w-2/3 min-w-[150px] h-9'>
          <Button
            label={ t( 'Verify code' ) }
            action={ verifyCode }
            disable={ !canVerifyCode }
            isLoading={ isLoading }
          />
        </div>
      </div>
    </div>
  );
};